import { FontSizeEnum, IconSizeEnum } from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Clock } from '@/components/Clock';
import { HelpTour } from '@/components/HelpTour/HelpTour';
import { Shorcut } from '@/components/Shorcut/Shorcut';
import { CLIP_REVIEW_STATES, TReviewStates } from '@/constants';
import { useGetBroadcastScheduleSegmentById } from '@/features/broadcast-schedule-program/api/get-broadcast-schedule-by-id-segment';
import { BroadcastScheduleProvider } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import { ESegmentToolkit } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/types';
import { BroadcastScheduleProgramHeaderButtonFilters } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramHeaderButtonFilters/BroadcastScheduleProgramHeaderButtonFilters';
import { BroadcastScheduleProgramHeaderFilters } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramHeaderFilters/BroadcastScheduleProgramHeaderFilters';
import { BroadcastScheduleProgramHeaderSearch } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramHeaderSearch/BroadcastScheduleProgramHeaderSearch';
import { useReportDrawerController } from '@/features/broadcast-schedule-program/components/ReportDrawer/hooks/useReportDrawerControllers';
import { ReportDrawer } from '@/features/broadcast-schedule-program/components/ReportDrawer/ReportDrawer';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useDropInactiveFilterSelections } from '@/features/mentions/components/MentionFilters/active-options/use-drop-inactive-filter-selections';
import { MentionShowSelectedFilters } from '@/features/mentions/components/MentionFilters/components/MentionShowSelectedFilters/MentionShowSelectedFilters';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';
import { useReport } from '@/stores/report-store';

import { useInfiniteClips } from '../api/get-infinite-clips';
import { clipsQueryKeys } from '../api/query-keys';
import { ClipBody } from '../components/ClipBody/ClipBody';
import { ClipHeaderName } from '../components/ClipHeaderName/ClipHeaderName';
import { useClipListFilters } from '../hooks/use-clip-list-filters';
import { useClipSelectionSync } from '../hooks/use-clip-selection-sync';
import { useClipsTour } from '../hooks/use-clips-tour';
import { useClipPermissions } from '../hooks/useClipPermissions';

export default function ClipsPage() {
  const {
    isHeaderVisible,
    videoControlRef,
    setActiveClip,
    handleResetVisibleBroadcastProgramCards,
    drawnFormReset,
    setClipItems,
    clipItems,
    activeClip,
    activeSegment,
    setActiveSegment,
    setSegmentItems,
    setPendingSeekTime,
    isSegmentPlayerMode,
    pinnedId,
    handleTooltikClick,
    setProgramNavigationState,
  } = useBroadcastScheduleProgram();

  const { canUpdate: canUpdateClips } = useClipPermissions();
  const { report } = useReport();
  const firstLoad = useRef(true);
  const activeClipIdRef = useRef<string | null>(null);
  const { handleCreateReport, handleOnClose } = useReportDrawerController();
  const { startTour } = useClipsTour();
  useDropInactiveFilterSelections('clips');
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const programIdParam = searchParams.get('program_id') || '';
  const programGteParam = searchParams.get('program_gte') || '';
  const programLteParam = searchParams.get('program_lte') || '';
  const queryKey = clipsQueryKeys.infinite();

  const { clearFilters } = useProgramBroadcastFilters();
  const clipFilters = useClipListFilters();

  const clipInfiniteData = useInfiniteClips({ filters: clipFilters });

  useClipSelectionSync(clipItems, JSON.stringify(clipFilters));

  const { data: programSegmentsData = null } = useGetBroadcastScheduleSegmentById({
    id: activeClip?.program_broadcast_id || '',
    config: {
      enabled: isSegmentPlayerMode && !!activeClip?.program_broadcast_id,
    },
  });

  useEffect(() => {
    activeClipIdRef.current = activeClip?.id ?? null;
  }, [activeClip?.id]);

  useEffect(() => {
    if (!programIdParam || !programGteParam || !programLteParam) return;

    setProgramNavigationState({
      id: programIdParam,
      gte: programGteParam,
      lte: programLteParam,
    });
  }, [programGteParam, programIdParam, programLteParam, setProgramNavigationState]);

  useEffect(() => {
    const data = clipInfiniteData?.data?.pages.flat() ?? [];
    if (!data) return;

    if (data.length === 0) {
      setActiveClip(null);
      setClipItems([]);
      return;
    }

    if (data && data[0]) {
      if (id) {
        const selectedClip = data?.find(clip => clip.id === id);
        if (selectedClip) {
          setActiveClip(selectedClip);
        }
      } else {
        const currentId = activeClipIdRef.current;
        const preservedClip = currentId ? data.find(clip => clip.id === currentId) : undefined;
        setActiveClip(preservedClip ?? data[0] ?? null);
      }

      setClipItems(data ?? []);
    }

    if (firstLoad.current) {
      firstLoad.current = false;
      // Only default to the edit panel when the user can edit clips. View-only roles
      // have the CORTES tab filtered out, so forcing it here would leave them on a
      // hidden panel; instead we leave it unpinned so they pick a visible tab.
      if (!pinnedId && canUpdateClips) {
        handleTooltikClick(ESegmentToolkit.CORTES);
      }
    }
  }, [clipInfiniteData.data, canUpdateClips]);

  useEffect(() => {
    if (programSegmentsData) {
      setSegmentItems(programSegmentsData);

      if (activeClip) {
        const clipStart = new Date(activeClip.start_datetime).getTime();
        const firstSeg = programSegmentsData.find(seg => {
          const segStart = new Date(seg.start_datetime).getTime();
          const segEnd = new Date(seg.end_datetime).getTime();
          return clipStart >= segStart && clipStart < segEnd;
        });

        if (firstSeg) {
          const segStartMs = new Date(firstSeg.start_datetime).getTime();
          const startOffsetSeconds = (clipStart - segStartMs) / 1000;

          if (activeSegment?.id === firstSeg.id) {
            videoControlRef.current?.seekToSecond(startOffsetSeconds > 0 ? startOffsetSeconds : 0);
          } else {
            setPendingSeekTime(startOffsetSeconds > 0 ? startOffsetSeconds : null);
          }

          setActiveSegment(firstSeg);
        }
      }
    } else {
      setSegmentItems([]);
      setPendingSeekTime(0);
    }
  }, [isSegmentPlayerMode, programSegmentsData, activeClip?.id]);

  useEffect(() => {
    return () => {
      clearFilters();
      setProgramNavigationState(null);
      handleResetVisibleBroadcastProgramCards();
      setActiveClip(null);
      setActiveSegment(null);
      drawnFormReset();
    };
  }, []);

  useEffect(() => {
    const primeraVez = localStorage.getItem('clipTourVisto');
    if (!primeraVez) {
      startTour();
      setTimeout(() => {
        if (videoControlRef.current) {
          videoControlRef.current?.stop();
        }
      }, 2000);
      localStorage.setItem('clipTourVisto', 'true');
    }
  }, []);

  return (
    <BroadcastScheduleProvider>
      <div className="flex flex-col gap-2" id="clips-page">
        <div className={clsx('flex gap-1', isHeaderVisible ? 'flex-col' : 'flex-col lg:flex-row')}>
          <ClipHeaderName />
          <div className="flex flex-col gap-2 lg:flex-row">
            {isHeaderVisible && <BroadcastScheduleProgramHeaderSearch />}
            {isHeaderVisible && (
              <BroadcastScheduleProgramHeaderFilters
                reviewState
                timeKeepingState
                reviewStateOptions={CLIP_REVIEW_STATES as unknown as TReviewStates[]}
                programs
                logo
                broadcast
                people
                workspaces
                tags
                activeOptionsSource="clips"
              />
            )}
          </div>
          <BroadcastScheduleProgramHeaderButtonFilters
            segments
            reviewStates={CLIP_REVIEW_STATES}
            isLoading={clipInfiniteData.isLoading}
            queryKey={queryKey}
          />
          {!isHeaderVisible && (
            <div className="ml-4 flex items-center gap-2">
              <Clock fontSize={FontSizeEnum.Md} iconSize={IconSizeEnum.SM} />
              <Shorcut />
              <HelpTour tour={startTour} />
            </div>
          )}
        </div>
        {!isHeaderVisible && (
          <MentionShowSelectedFilters
            reviewStateOptions={CLIP_REVIEW_STATES as unknown as TReviewStates[]}
          />
        )}
        <ClipBody clipsData={clipInfiniteData} />
      </div>

      <ReportDrawer isOpen={report} onClose={handleOnClose} onSubmit={handleCreateReport} />
    </BroadcastScheduleProvider>
  );
}
